import { useInfiniteQuery } from 'react-query'
import hash from 'object-hash'
import { InventoryFiltersDto, InventoryDto } from '@merchx-v2/shared-types'
import axios from 'utils/axios'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'

const DEFAULT_PAGE_SIZE = 30

export type InventoryPage = {
  items: InventoryDto[]
  total: number
  currentPage: number
  pageSize: number
  filters: InventoryFiltersDto
}

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'inventoryListPage', InventoryPage>

const FETCH_INVENTORIES_LIST = `
  query fetchInventoryListPage ($filters: InventoryFilters!, $page: Int, $size: Int) {
    inventoryListPage(filters: $filters, page: $page, size: $size) {
      items {
        productId
        product {
          sellWhenOutOfStock
          displayName
        }
        variantKey
        stock
      }
      total
    }
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type PageParam = {
  page?: number
  size?: number
  filters: InventoryFiltersDto
}

type FetchInventoryListArgs = {
  queryKey: string | string[]
  pageParam?: PageParam
}

type FetchInventoryList = (args: FetchInventoryListArgs) => Promise<InventoryPage>

const fetchInventoryList: FetchInventoryList = async ({ pageParam }) => {
  const { page = 1, size = DEFAULT_PAGE_SIZE, filters } = pageParam || {}
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: FETCH_INVENTORIES_LIST,
      variables: { page, size, filters }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { inventoryListPage } = data
    if (!inventoryListPage) {
      throw new Error("Can't get stock list page!")
    }

    return {
      items: inventoryListPage.items,
      total: inventoryListPage.total,
      currentPage: page,
      pageSize: size,
      filters
    }
  } catch (error) {
    throw new Error(extractErrorInfo(error))
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

type GetNextPageParam = (args: InventoryPage) => PageParam

type InventoryListEvents = {
  keepPreviousData: boolean
  getPreviousPageParam: GetNextPageParam
  getNextPageParam: GetNextPageParam
}

const inventoryListEvents: InventoryListEvents = {
  keepPreviousData: true,
  getPreviousPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, filters }) => {
    if (currentPage > 1) {
      return {
        page: currentPage - 1,
        size: pageSize,
        filters
      }
    }

    return undefined
  },
  getNextPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, total = 0, filters }) => {
    if (pageSize * currentPage < total) {
      return {
        page: currentPage + 1,
        size: pageSize,
        filters
      }
    }
    return undefined
  }
}

export const useInventoryList = (filters?: InventoryFiltersDto) => {
  const cacheKey = ['inventoryList', hash(filters)]

  return useInfiniteQuery(
    cacheKey,
    (prev) =>
      fetchInventoryList({
        queryKey: cacheKey,
        pageParam: { ...prev.pageParam, filters }
      }),
    inventoryListEvents
  )
}
